import AnomalyEvents from "./RestApiClient/anomalyEvents"
import CheckIns from "./RestApiClient/checkIns"
import Deploys from "./RestApiClient/deploys"
import Events from "./RestApiClient/events"
import Kubernetes from "./RestApiClient/kubernetes"
import Logs from "./RestApiClient/logs"
import Metrics from "./RestApiClient/metrics"
import Tracing from "./RestApiClient/tracing"

const ENV_IS_TEST = process.env.NODE_ENV === "test"

class RestApiClient {
  baseUrl
  token
  constructor(baseUrl, token) {
    this.baseUrl = baseUrl
    this.token = token
    this.metrics = new Metrics(this)
    this.anomalyEvents = new AnomalyEvents(this)
    this.events = new Events(this)
    this.tracing = new Tracing(this)
    this.checkIns = new CheckIns(this)
    this.deploys = new Deploys(this)
    this.kubernetes = new Kubernetes(this)
    this.logs = new Logs(this)
  }

  async customFetch(uri, options = {}) {
    if (!ENV_IS_TEST) {
      try {
        appsignal.addBreadcrumb({
          category: "REST",
          action: `REST query: ${uri}`,
        })
      } catch (e) {
        console.error("Failed to parse request body", e)
      }
    }

    const { signal, ...restOptions } = options

    return fetch(`${this.baseUrl}${uri}`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-AppSignal-Internal": "1",
        "X-AppSignal-Source": typeof window !== "undefined" ? window.location.pathname : "unknown",
      },
      signal,
      ...restOptions,
    })
  }

  // Fetch for Server-Sent Events with streaming headers
  async fetchStream(uri, options = {}) {
    if (!ENV_IS_TEST) {
      try {
        appsignal.addBreadcrumb({
          category: "SSE",
          action: `SSE query: ${uri}`,
        })
      } catch (e) {
        console.error("Failed to log SSE breadcrumb", e)
      }
    }

    const { signal, ...restOptions } = options

    return fetch(`${this.baseUrl}${uri}`, {
      headers: {
        Authorization: `Bearer ${this.token}`,
        Accept: "text/event-stream",
        "Content-Type": "application/json",
        "X-AppSignal-Internal": "1",
        "X-AppSignal-Source": typeof window !== "undefined" ? window.location.pathname : "unknown",
      },
      signal,
      ...restOptions,
    })
  }

  // Throw on non-2xx status codes
  async checkStatus(response) {
    if (!response.ok) {
      const error = new Error(`${response.status} ${response.statusText}`)
      error.response = response
      try {
        error.body = await response.json()
      } catch (e) {
        console.error("Failed to parse error response body", e)
      }
      throw error
    }
    return response
  }

  // 204 is No-content
  parseJson(response) {
    return response.status === 204 ? {} : response.json()
  }

  // Record how long a request ran before it failed, so the retry policy can
  // skip retrying slow failures. See utils/apiErrors#retryOnlyServerErrors.
  async timed(request) {
    const startedAt = performance.now()

    try {
      return await request()
    } catch (error) {
      if (error instanceof Error && error.durationMs === undefined) {
        error.durationMs = performance.now() - startedAt
      }
      throw error
    }
  }

  async get(url, options = {}) {
    return this.timed(() =>
      this.customFetch(url, { ...options, method: "GET" })
        .then(this.checkStatus)
        .then(this.parseJson),
    )
  }

  // GET request for streaming - returns raw response for stream processing
  async getStream(url, options = {}) {
    return this.timed(() =>
      this.fetchStream(url, { ...options, method: "GET" }).then(this.checkStatus),
    )
  }

  // POST request for streaming with JSON body
  async postStream(url, data, options = {}) {
    const body = JSON.stringify(data)
    return this.timed(() =>
      this.fetchStream(url, { ...options, method: "POST", body }).then(this.checkStatus),
    )
  }

  async requestWithBody(method, url, data, options) {
    const body = JSON.stringify(data)
    return this.timed(() =>
      this.customFetch(url, { ...options, method, body })
        .then(this.checkStatus)
        .then(this.parseJson),
    )
  }

  // delete is a reserved word
  async destroy(url, options = {}) {
    return this.timed(() =>
      this.customFetch(url, { ...options, method: "DELETE" })
        .then(this.checkStatus)
        .then(this.parseJson),
    )
  }

  async post(url, data, options) {
    return this.requestWithBody("POST", url, data, options)
  }
  async put(url, data, options) {
    return this.requestWithBody("PUT", url, data, options)
  }
  async patch(url, data, options) {
    return this.requestWithBody("PATCH", url, data, options)
  }
}

export default RestApiClient
